/**
 * Sidebar - Organism component
 * Navigation sidebar with collapsible sections
 * WCAG 2.1 AA compliant
 */

import React, { useState } from 'react';
import { Icon, Badge } from '@/components/atoms';

export interface SidebarItem {
  id: string;
  label: string;
  icon?: string;
  href?: string;
  badge?: { text: string; variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' };
  children?: SidebarItem[];
}

export interface SidebarProps {
  /**
   * Sidebar navigation items
   */
  items: SidebarItem[];
  /**
   * Active item ID
   */
  activeId?: string;
  /**
   * Whether sidebar is collapsed
   */
  collapsed?: boolean;
  /**
   * Callback when item is clicked
   */
  onItemClick?: (item: SidebarItem) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  items,
  activeId,
  collapsed = false,
  onItemClick,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpand = (itemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const renderItem = (item: SidebarItem, depth = 0) => {
    const isActive = item.id === activeId;
    const isExpanded = expandedItems.has(item.id);
    const hasChildren = item.children && item.children.length > 0;

    return (
      <li key={item.id}>
        <div
          className={`flex items-center justify-between px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            isActive
              ? 'bg-primary-100 text-primary-700'
              : 'text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900'
          } ${depth > 0 ? 'ml-4' : ''}`}
          style={{ paddingLeft: `${(depth + 1) * 16}px` }}
        >
          <button
            onClick={() => {
              if (hasChildren) {
                toggleExpand(item.id);
              } else if (item.href) {
                onItemClick?.(item);
              }
            }}
            className="flex items-center gap-3 flex-1 text-left focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
            aria-expanded={hasChildren ? isExpanded : undefined}
            aria-current={isActive ? 'page' : undefined}
          >
            {item.icon && !collapsed && (
              <Icon name={item.icon as any} size={20} aria-hidden />
            )}
            {!collapsed && (
              <span className="flex-1">{item.label}</span>
            )}
            {!collapsed && item.badge && (
              <Badge variant={item.badge.variant}>{item.badge.text}</Badge>
            )}
            {!collapsed && hasChildren && (
              <Icon
                name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
                size={16}
                aria-hidden
              />
            )}
          </button>
        </div>

        {hasChildren && isExpanded && !collapsed && (
          <ul className="mt-1 space-y-1">
            {item.children!.map((child) => renderItem(child, depth + 1))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <nav
      className={`h-full bg-white border-r border-neutral-200 transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
      aria-label="Sidebar navigation"
    >
      <div className="p-4">
        <ul className="space-y-1">
          {items.map((item) => renderItem(item))}
        </ul>
      </div>
    </nav>
  );
};
